import * as React from 'react';
import { useDebouncedValue } from '../../../hooks/use-debounced-value';
import { buildEmptyFilters, resolveDateRangeFilters } from './list-page.helpers';
import type { IListPageFilterConfig } from './list-page.types';

const DEFAULT_SEARCH_DEBOUNCE_MS = 250;

export interface IUseListPageInputStateInput {
	filterConfigs?: IListPageFilterConfig[];
	searchDebounceMs?: number;
}

export interface IUseListPageInputStateResult {
	/** Raw search input value driven by the search field (consumer-controlled). */
	search: string;
	setSearch: React.Dispatch<React.SetStateAction<string>>;
	/**
	 * `search` after the configured debounce window has elapsed. Use this when the data layer's
	 * cache key is derived from the search term (e.g. a hook-backed `useData` whose React-Query
	 * key includes search). For `apiMethod`-backed pages, `useListPageData` already debounces
	 * internally; pass `search` instead.
	 */
	debouncedSearch: string;
	/**
	 * The applied filter map. Values are still in their "raw" form — `type: 'date-range'`
	 * options are stored as the option's `value` string, not the resolved `IListPageDateRange`.
	 * Suitable for a React-Query cache key because the value is stable across renders.
	 */
	appliedFilters: Record<string, unknown>;
	setAppliedFilters: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
	/**
	 * `appliedFilters` with `type: 'date-range'` selections resolved via the option's `range()`
	 * callback. Memoized on `[appliedFilters, filterConfigs]`. Use this when the data layer's
	 * fetch payload (not its cache key) needs the resolved range — typically the hook-backed
	 * `useData` path.
	 */
	resolvedFilters: Record<string, unknown>;
	/**
	 * Stable callback that resolves an arbitrary filter map against the latest `filterConfigs`.
	 * Use this from inside an `apiMethod` wrapper so "now-relative" date-range options
	 * (e.g. "Last 7 days") anchor to the moment the request fires rather than the moment the
	 * user applied the filter — the `apiMethod`-backed path keeps the raw `appliedFilters` in
	 * the cache key and resolves at fetch time via this callback.
	 */
	resolveFilters: (
		filters: Record<string, unknown> | undefined
	) => Record<string, unknown> | undefined;
}

/**
 * Centralizes the search + applied-filter input pipeline shared by every variant of
 * `<ListPage>`. Owns:
 *
 * - The `search` state and its debounced projection (`debouncedSearch`).
 * - The `appliedFilters` state, seeded from the filter configs' empty shape.
 * - Date-range resolution: a memoized `resolvedFilters` for cache-key-derived data layers
 *   (hook-backed) and a stable `resolveFilters` callback for fetch-time resolution
 *   (api-method-backed).
 *
 * Both `<ListPage>` branches (`ListPageWithApi`, `ListPageWithHook`) consume this so search,
 * filters, and date-range semantics stay in lockstep. Future cross-cutting features
 * (URL sync, debounce around `setAppliedFilters`, optimistic clear-on-tab-change, persistence)
 * land here once and propagate to every branch — and to any future consumer composing
 * `<ListPageLayout>` primitives directly.
 */
export const useListPageInputState = ({
	filterConfigs,
	searchDebounceMs,
}: IUseListPageInputStateInput): IUseListPageInputStateResult => {
	const [search, setSearch] = React.useState('');
	const [appliedFilters, setAppliedFilters] = React.useState<Record<string, unknown>>(() =>
		buildEmptyFilters(filterConfigs ?? [])
	);

	const debouncedSearch = useDebouncedValue(search, searchDebounceMs ?? DEFAULT_SEARCH_DEBOUNCE_MS);

	const filterConfigsRef = React.useRef(filterConfigs);
	filterConfigsRef.current = filterConfigs;

	const resolveFilters = React.useCallback(
		(filters: Record<string, unknown> | undefined) =>
			resolveDateRangeFilters(filters, filterConfigsRef.current ?? []),
		[]
	);

	const resolvedFilters = React.useMemo(
		() => resolveDateRangeFilters(appliedFilters, filterConfigs ?? []) ?? appliedFilters,
		[appliedFilters, filterConfigs]
	);

	return {
		search,
		setSearch,
		debouncedSearch,
		appliedFilters,
		setAppliedFilters,
		resolvedFilters,
		resolveFilters,
	};
};
